/**
 * Access Control — roles, permissions & assignments (Phase 8, P4B UI).
 *
 * ADMIN view (ROLE_MANAGE / USER_MANAGE): the tenant role × permission matrix
 * with non-grantable permissions disabled, plus create/delete custom roles and
 * per-member role assignment.
 *
 * ROOT view (PLATFORM_ROLE_MANAGE / PLATFORM_USER_MANAGE): baseline templates,
 * the full permission catalog, and grant/revoke of tenant ADMIN + ROOT.
 *
 * The UI mirrors the server guardrails (anti-escalation, protected system
 * roles) but the server re-checks every mutation (§4.7) — this is a hint layer.
 */

import React from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Checkbox, Button, Chip, Alert, Tooltip, IconButton, Tabs, Tab, TextField,
  Stack, Divider, FormControl, InputLabel, Select, MenuItem,
  FormGroup, FormControlLabel,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import SecurityIcon from '@mui/icons-material/Security';
import swal from 'sweetalert';
import Colors from '../../../components/ui/Colors';
import CustomLoader from '../../../components/CustomLoader';
import { isRoot, hasAnyPermission } from '../../../components/AccessControl';
import {
  listTenantRoles, getGrantablePermissions, createTenantRole,
  updateTenantRolePermissions, deleteTenantRole, setMembershipRoles,
  listTenantMemberships, admitMember, deactivateMember,
  promoteMembership, demoteMembership,
  listPlatformRoles, listPlatformPermissionCatalog, updateTemplatePermissions,
  grantTenantAdmin, revokeTenantAdmin, appointRoot, revokeRoot, listAllTenants,
} from '../../../utilityFunctions/PlatformAdminUtil';

const PROTECTED_ROLES = ['USER', 'ADMIN', 'ROOT'];
const isProtected = (name) => PROTECTED_ROLES.indexOf(name) !== -1;
const asArray = (v) => (Array.isArray(v) ? v : (v ? Array.from(v) : []));

/**
 * Editable role × permission matrix. `grantable` gates which cells the caller
 * may toggle; protected system roles are read-only. Saves per role.
 */
function RoleMatrix({ roles, grantable, allPermissions, onSave, onDelete, canDelete, allowTemplateEdit }) {
  // roleId -> Set(permission)
  const [draft, setDraft] = React.useState({});
  const [dirty, setDirty] = React.useState({});

  React.useEffect(() => {
    const d = {};
    roles.forEach((r) => { d[r.id] = new Set(asArray(r.permissions)); });
    setDraft(d);
    setDirty({});
  }, [roles]);

  const grantableSet = React.useMemo(() => new Set(asArray(grantable)), [grantable]);

  const toggle = (roleId, perm) => {
    setDraft((prev) => {
      const next = { ...prev };
      const set = new Set(next[roleId] || []);
      if (set.has(perm)) set.delete(perm); else set.add(perm);
      next[roleId] = set;
      return next;
    });
    setDirty((prev) => ({ ...prev, [roleId]: true }));
  };

  if (roles.length === 0) return <Alert severity="info">No roles to display.</Alert>;

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader sx={{ '& th': { fontWeight: 700, color: 'text.secondary' } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ position: 'sticky', left: 0, background: '#fff', zIndex: 2, minWidth: 160 }}>Role</TableCell>
            {allPermissions.map((p) => (
              <TableCell key={p} align="center" sx={{ whiteSpace: 'nowrap', fontSize: '0.68rem' }}>
                {p}{!grantableSet.has(p) && <Tooltip title="Not grantable by you"><span> 🔒</span></Tooltip>}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ position: 'sticky', right: 0, background: '#fff', zIndex: 2 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {roles.map((r) => {
            // A protected name (USER/ADMIN/ROOT) is normally read-only. On the Platform
            // tab (allowTemplateEdit), baseline templates & platform roles (tenantId null)
            // are editable — the server authorizes updateTemplatePermissions for exactly
            // those (tenant_id NULL) — so unlock them there while keeping tenant-scoped
            // protected rows locked.
            const editableTemplate = allowTemplateEdit && r.tenantId == null;
            const locked = isProtected(r.name) && !editableTemplate;
            const set = draft[r.id] || new Set();
            return (
              <TableRow key={r.id}>
                <TableCell sx={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, fontWeight: 600 }}>
                  {r.name}
                  {locked && <Chip size="small" label="system" sx={{ ml: 0.5 }} />}
                  {r.tenantId == null && <Chip size="small" label="template" color="info" sx={{ ml: 0.5 }} />}
                </TableCell>
                {allPermissions.map((p) => {
                  const cellEditable = !locked && grantableSet.has(p);
                  return (
                    <TableCell key={p} align="center" sx={{ p: 0.25 }}>
                      <Checkbox
                        size="small"
                        checked={set.has(p)}
                        disabled={!cellEditable}
                        onChange={() => toggle(r.id, p)}
                      />
                    </TableCell>
                  );
                })}
                <TableCell align="right" sx={{ position: 'sticky', right: 0, background: '#fff', zIndex: 1, whiteSpace: 'nowrap' }}>
                  {!locked && (
                    <Tooltip title="Save permissions"><span>
                      <IconButton size="small" color="primary" disabled={!dirty[r.id]}
                        onClick={() => onSave(r.id, Array.from(set))}>
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    </span></Tooltip>
                  )}
                  {!locked && canDelete && r.tenantId != null && (
                    <Tooltip title="Delete role"><span>
                      <IconButton size="small" color="error" onClick={() => onDelete(r)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span></Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// ── ADMIN: tenant roles ────────────────────────────────────────────────────

function TenantRolesTab({ activeTenantSlug, activeTenantId }) {
  const [roles, setRoles] = React.useState([]);
  const [grantable, setGrantable] = React.useState([]);
  const [selfDelegation, setSelfDelegation] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [newName, setNewName] = React.useState('');
  const [memberships, setMemberships] = React.useState([]);
  const [admitUsername, setAdmitUsername] = React.useState('');
  const [selectedMembershipId, setSelectedMembershipId] = React.useState('');
  const [checkedRoleIds, setCheckedRoleIds] = React.useState([]);
  const [baselineBusyId, setBaselineBusyId] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    const [rolesRes, permsRes, membersRes] = await Promise.all([
      listTenantRoles(), getGrantablePermissions(), listTenantMemberships(),
    ]);
    setLoading(false);
    if (!rolesRes.ok) { setError(rolesRes.error); return; }
    setError(null);
    setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
    setMemberships(membersRes.ok && Array.isArray(membersRes.data) ? membersRes.data : []);
    if (permsRes.ok && permsRes.data) {
      setGrantable(asArray(permsRes.data.grantable));
      setSelfDelegation(!!permsRes.data.selfDelegationAllowed);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Custom tenant roles the ADMIN can assign — baseline USER/ADMIN templates
  // carry tenantId null and are excluded (managed via admit / the Platform tab).
  const assignableRoles = React.useMemo(
    () => roles.filter((r) => r.scope === 'TENANT' && r.tenantId === activeTenantId),
    [roles, activeTenantId],
  );

  const handleSelectMember = (membershipId) => {
    setSelectedMembershipId(membershipId);
    const m = memberships.find((x) => x.membershipId === membershipId);
    const assignableIds = new Set(assignableRoles.map((r) => r.id));
    const held = m ? asArray(m.roles).map((r) => r.id).filter((id) => assignableIds.has(id)) : [];
    setCheckedRoleIds(held);
  };

  const toggleRoleId = (roleId) => {
    setCheckedRoleIds((prev) => (prev.indexOf(roleId) !== -1
      ? prev.filter((x) => x !== roleId)
      : [...prev, roleId]));
  };

  const allPermissions = React.useMemo(() => {
    const s = new Set();
    roles.forEach((r) => asArray(r.permissions).forEach((p) => s.add(p)));
    asArray(grantable).forEach((p) => s.add(p));
    return Array.from(s).sort();
  }, [roles, grantable]);

  const saveRole = async (roleId, permissions) => {
    const res = await updateTenantRolePermissions(roleId, permissions);
    if (!res.ok) { swal({ title: 'Save failed', text: res.error, icon: 'error' }); return; }
    await load();
  };

  const removeRole = async (role) => {
    const ok = await swal({ title: `Delete role "${role.name}"?`, icon: 'warning', buttons: ['Cancel', 'Delete'], dangerMode: true });
    if (!ok) return;
    const res = await deleteTenantRole(role.id);
    if (!res.ok) { swal({ title: 'Delete failed', text: res.error, icon: 'error' }); return; }
    await load();
  };

  const addRole = async () => {
    const name = newName.trim();
    if (!name) { swal({ title: 'Enter a role name', icon: 'info' }); return; }
    const res = await createTenantRole(name, []);
    if (!res.ok) { swal({ title: 'Create failed', text: res.error, icon: 'error' }); return; }
    setNewName('');
    await load();
  };

  const admit = async () => {
    const uname = admitUsername.trim();
    if (!uname) { swal({ title: 'Enter a username', icon: 'info' }); return; }
    const res = await admitMember(uname);
    if (!res.ok) { setError(res.error); return; }
    setError(null);
    setAdmitUsername('');
    await load();
  };

  const removeMember = async (m) => {
    const ok = await swal({
      title: `Remove ${m.username} from this tenant?`,
      text: 'This deactivates their membership in the active tenant.',
      icon: 'warning', buttons: ['Cancel', 'Remove'], dangerMode: true,
    });
    if (!ok) return;
    const res = await deactivateMember(m.membershipId);
    if (!res.ok) { setError(res.error); return; }
    setError(null);
    await load();
  };

  // Baseline USER↔ADMIN toggle. `promote` = USER→ADMIN, else ADMIN→USER.
  // The server owns every guard (LAST_ADMIN / SELF_LOCKOUT / SCOPE_VIOLATION);
  // on failure we surface its message verbatim in the error Alert.
  const toggleBaseline = async (m, promote) => {
    const ok = await swal({
      title: promote ? `Make ${m.username} an ADMIN?` : `Make ${m.username} a USER?`,
      text: promote
        ? 'Grants the tenant ADMIN baseline role.'
        : 'Removes the tenant ADMIN baseline role, leaving USER.',
      icon: 'warning',
      buttons: ['Cancel', promote ? 'Make ADMIN' : 'Make USER'],
    });
    if (!ok) return;
    setBaselineBusyId(m.membershipId);
    const res = promote
      ? await promoteMembership(m.membershipId)
      : await demoteMembership(m.membershipId);
    setBaselineBusyId(null);
    if (!res.ok) { setError(res.error); return; }
    setError(null);
    swal({ title: promote ? 'Promoted to ADMIN' : 'Demoted to USER', icon: 'success' });
    await load();
  };

  const applyRoles = async () => {
    if (!selectedMembershipId) { swal({ title: 'Select a member', icon: 'info' }); return; }
    const res = await setMembershipRoles(selectedMembershipId, checkedRoleIds);
    if (!res.ok) { setError(res.error); return; }
    setError(null);
    swal({ title: 'Roles updated', icon: 'success' });
    await load();
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Roles for the active tenant.{activeTenantSlug ? ` Managing roles for tenant: ${activeTenantSlug}` : ''}
      </Alert>
      <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <TextField size="small" label="New role name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addRole}>Create role</Button>
        <Button variant="text" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
        {!selfDelegation && <Chip size="small" label="self-delegation off" />}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <CustomLoader />}

      {!loading && (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 3 }}>
          <RoleMatrix
            roles={roles} grantable={grantable} allPermissions={allPermissions}
            onSave={saveRole} onDelete={removeRole} canDelete
          />
        </Paper>
      )}

      {!loading && (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Members</Typography>
          {memberships.length === 0 ? (
            <Alert severity="info">No members to display.</Alert>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& th': { fontWeight: 700, color: 'text.secondary' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Roles</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {memberships.map((m) => (
                    <TableRow key={m.membershipId}>
                      <TableCell sx={{ fontWeight: 600 }}>{m.username}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{m.email || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={m.status}
                          color={m.status === 'ACTIVE' ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                          {asArray(m.roles).map((r) => (
                            <Chip key={r.id} size="small" label={r.name} variant="outlined" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {m.status === 'ACTIVE' && (() => {
                          const roleNames = asArray(m.roles).map((r) => r.name);
                          const hasAdmin = roleNames.indexOf('ADMIN') !== -1;
                          const hasUser = roleNames.indexOf('USER') !== -1;
                          const busy = baselineBusyId === m.membershipId;
                          if (hasAdmin) {
                            return (
                              <Button size="small" variant="outlined" color="warning" disabled={busy}
                                onClick={() => toggleBaseline(m, false)} sx={{ mr: 1 }}>
                                Make USER
                              </Button>
                            );
                          }
                          if (hasUser) {
                            return (
                              <Button size="small" variant="outlined" disabled={busy}
                                onClick={() => toggleBaseline(m, true)} sx={{ mr: 1 }}>
                                Make ADMIN
                              </Button>
                            );
                          }
                          return null;
                        })()}
                        <Tooltip title="Remove from tenant"><span>
                          <IconButton size="small" color="error" onClick={() => removeMember(m)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Admit a member</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Admits an existing user to this tenant. Ask the user to register first if they don't have an account.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: 'center' }}>
            <TextField size="small" label="Username" value={admitUsername}
              onChange={(e) => setAdmitUsername(e.target.value)} sx={{ flex: 1, minWidth: 180 }} />
            <Button variant="outlined" startIcon={<AddIcon />} onClick={admit}>Admit</Button>
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, maxWidth: 640 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Assign roles to a member</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Pick a member, then check the custom tenant roles they should have. The server rejects roles outside your grantable set.
        </Typography>
        <FormControl size="small" sx={{ minWidth: 280, mb: 1.5 }}>
          <InputLabel id="assign-member-select-label">Member</InputLabel>
          <Select
            labelId="assign-member-select-label"
            label="Member"
            value={selectedMembershipId}
            onChange={(e) => handleSelectMember(e.target.value)}
          >
            {memberships.map((m) => (
              <MenuItem key={m.membershipId} value={m.membershipId}>
                {`${m.username}${m.email ? ' — ' + m.email : ''}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedMembershipId !== '' && (
          assignableRoles.length === 0 ? (
            <Alert severity="info">
              No custom roles yet. Create one above to assign it. Baseline USER/ADMIN are managed via
              membership admit and the Platform tab.
            </Alert>
          ) : (
            <Box>
              <FormGroup>
                {assignableRoles.map((r) => (
                  <FormControlLabel
                    key={r.id}
                    control={(
                      <Checkbox
                        size="small"
                        checked={checkedRoleIds.indexOf(r.id) !== -1}
                        onChange={() => toggleRoleId(r.id)}
                      />
                    )}
                    label={r.name}
                  />
                ))}
              </FormGroup>
              <Button variant="contained" onClick={applyRoles} sx={{ mt: 1, backgroundColor: Colors.primary }}>
                Apply roles
              </Button>
            </Box>
          )
        )}
      </Paper>
    </Box>
  );
}

// ── ROOT: platform templates + user administration ─────────────────────────

function PlatformTab() {
  const [roles, setRoles] = React.useState([]);
  const [catalog, setCatalog] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [username, setUsername] = React.useState('');
  const [tenantId, setTenantId] = React.useState('');
  const [tenants, setTenants] = React.useState([]);

  const load = React.useCallback(async () => {
    setLoading(true);
    const [rolesRes, catRes, tenantsRes] = await Promise.all([
      listPlatformRoles(), listPlatformPermissionCatalog(), listAllTenants(),
    ]);
    setLoading(false);
    setTenants(tenantsRes.ok && Array.isArray(tenantsRes.data) ? tenantsRes.data : []);
    if (!rolesRes.ok) { setError(rolesRes.error); return; }
    setError(null);
    setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
    setCatalog(catRes.ok ? asArray(catRes.data) : []);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const allPermissions = React.useMemo(() => {
    const s = new Set(asArray(catalog));
    roles.forEach((r) => asArray(r.permissions).forEach((p) => s.add(p)));
    return Array.from(s).sort();
  }, [roles, catalog]);

  const saveTemplate = async (roleId, permissions) => {
    const res = await updateTemplatePermissions(roleId, permissions);
    if (!res.ok) { swal({ title: 'Save failed', text: res.error, icon: 'error' }); return; }
    await load();
  };

  const doUserAction = async (label, fn) => {
    if (!username.trim()) { swal({ title: 'Enter a username', icon: 'info' }); return; }
    const res = await fn();
    if (!res.ok) { swal({ title: `${label} failed`, text: res.error, icon: 'error' }); return; }
    swal({ title: `${label} done`, icon: 'success' });
  };

  const tId = () => (tenantId ? parseInt(tenantId, 10) : null);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Platform-level roles, permission catalog, and baseline templates. To manage a specific tenant's
        own roles, switch into that tenant using the tenant switcher in the top bar.
      </Alert>
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <Button variant="text" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <CustomLoader />}

      {!loading && (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Baseline templates & platform roles</Typography>
          <RoleMatrix
            roles={roles} grantable={allPermissions} allPermissions={allPermissions}
            onSave={saveTemplate} onDelete={() => {}} canDelete={false} allowTemplateEdit
          />
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, maxWidth: 720 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>User administration</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField size="small" label="Username" value={username} onChange={(e) => setUsername(e.target.value)} sx={{ flex: 1 }} />
          {tenants.length > 0 ? (
            <FormControl size="small" sx={{ width: 260 }}>
              <InputLabel id="tenant-admin-select-label">Tenant</InputLabel>
              <Select
                labelId="tenant-admin-select-label"
                label="Tenant"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
              >
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{`${t.displayName || t.slug} (#${t.id})`}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField size="small" label="Tenant id (for admin)" value={tenantId}
              onChange={(e) => setTenantId(e.target.value.replace(/[^0-9]/g, ''))} sx={{ width: 180 }}
              helperText="Numeric tenant ID (database id)" />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Grants the tenant ADMIN role, admitting the user to the tenant if they aren't a member yet.
        </Typography>
        <Divider sx={{ mb: 1.5 }} />
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" size="small"
            onClick={() => doUserAction('Grant tenant ADMIN', () => grantTenantAdmin(username, tId()))}>Grant tenant ADMIN</Button>
          <Button variant="outlined" size="small" color="warning"
            onClick={() => doUserAction('Revoke tenant ADMIN', () => revokeTenantAdmin(username, tId()))}>Revoke tenant ADMIN</Button>
          <Button variant="outlined" size="small" color="secondary"
            onClick={() => doUserAction('Appoint ROOT', () => appointRoot(username))}>Appoint ROOT</Button>
          <Button variant="outlined" size="small" color="error"
            onClick={() => doUserAction('Revoke ROOT', () => revokeRoot(username))}>Revoke ROOT</Button>
        </Stack>
      </Paper>
    </Box>
  );
}

export default function AccessControlScreen(props) {
  const loginUser = props && props.loginUser;
  const root = isRoot(loginUser);
  // Purely permission-based: ROOT only gains tenant ROLE_MANAGE/USER_MANAGE after
  // switching INTO a tenant it can administer. In the default tenant ROOT holds
  // only PLATFORM_* perms, so the Tenant roles tab (tenant-scoped, 403 for ROOT
  // there) stays hidden and ROOT lands on its real surface — the Platform tab.
  const canManageTenant = hasAnyPermission(loginUser, ['ROLE_MANAGE', 'USER_MANAGE']);
  const activeTenantSlug = (loginUser && loginUser.activeTenantSlug) || null;
  const activeTenantId = (loginUser && loginUser.activeTenantId) || null;

  // Visible admin surfaces in a stable order; the FIRST one is the landing tab so
  // no principal ever lands on a surface it cannot call.
  const visibleTabs = [];
  if (canManageTenant) visibleTabs.push('tenant');
  if (root) visibleTabs.push('platform');

  const [tab, setTab] = React.useState(0);
  const selected = tab < visibleTabs.length ? tab : 0;
  const activeKey = visibleTabs[selected];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1250, mx: 'auto', mt: '5rem' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <SecurityIcon sx={{ color: Colors.primary }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: Colors.primary }}>Access control</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage roles, permissions and assignments. Non-grantable permissions are locked; every change is
        re-checked by the server.
      </Typography>

      {visibleTabs.length === 0 && (
        <Alert severity="info">
          No administration surfaces are available for the current context.
        </Alert>
      )}

      {visibleTabs.length > 0 && (
        <Tabs value={selected} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
          {visibleTabs.map((k) => (
            <Tab key={k} label={k === 'tenant' ? 'Tenant roles' : 'Platform'} />
          ))}
        </Tabs>
      )}

      {activeKey === 'tenant' && <TenantRolesTab activeTenantSlug={activeTenantSlug} activeTenantId={activeTenantId} />}
      {activeKey === 'platform' && <PlatformTab />}
    </Box>
  );
}
